import React from "react";
import PolicyPage from "../../components/policy/PolicyPage";
import { BUSINESS } from "../../config/business";

export default function Privacy() {
  const sections = [
    {
      heading: "Who we are and what this policy covers",
      body: [
        `${BUSINESS.legalName}, trading as ${BUSINESS.brand}, is the data fiduciary responsible for the personal data collected through this website, our enquiry forms, our WhatsApp Business number and our showroom at ${BUSINESS.operatingAddress}.`,
        `This policy explains what personal data we collect, why we collect it, who we share it with, how long we keep it, and what rights you have. It applies to everyone who visits this website, submits an enquiry, books a product, buys from us, or brings a vehicle in for service.`,
        `We handle personal data in line with the Digital Personal Data Protection Act, 2023 and the Information Technology Act, 2000 and rules made under it.`,
      ],
    },
    {
      heading: "What we collect",
      body: [
        {
          table: {
            head: ["Category", "Examples", "Why we need it"],
            rows: [
              [
                "Identity and contact data",
                "Name, mobile number, WhatsApp number, email address",
                "To respond to your enquiry, confirm a booking, arrange delivery and provide after-sales support",
              ],
              [
                "Address data",
                "Delivery address, billing address, PIN code",
                "To deliver the product, raise a valid GST invoice, and confirm you are within our service area",
              ],
              [
                "Order and transaction data",
                "Products booked, price, booking advance and balance, invoice number, payment status, payment reference ID",
                "To process and fulfil your order, maintain accounts, and meet tax and record-keeping obligations",
              ],
              [
                "Product and service data",
                "Frame number, battery serial number, model, purchase date, service and repair history, warranty claims",
                "To honour warranty claims, service your vehicle correctly, and trace safety recalls",
              ],
              [
                "Finance facilitation data",
                "Documents you choose to submit for an EMI application, where we help you apply",
                "Only to pass to the financier you have chosen, with your consent",
              ],
              [
                "Communications",
                "Enquiry form submissions, emails, WhatsApp messages, call notes",
                "To keep a record of what was agreed and to improve our service",
              ],
              [
                "Technical data",
                "IP address, browser and device type, pages viewed, referring page",
                "To keep the site secure and working, and to understand which products people look at",
              ],
            ],
          },
        },
        {
          note:
            "We do not collect or store your full card number, CVV, UPI PIN, net-banking password or any other payment credential. Those are entered directly into a PCI-DSS compliant payment gateway and never reach our systems. We see only a masked reference and the payment status.",
        },
      ],
    },
    {
      heading: "How we collect it",
      body: [
        {
          list: [
            "Directly from you — when you fill in an enquiry form, message us on WhatsApp, call us, or visit the showroom.",
            "Automatically — through cookies and similar technologies when you browse this website (see section 8).",
            "From third parties — payment status from our payment gateway, and application status from a financier where you have applied for EMI through us.",
          ],
        },
        `The enquiry form on our Contact page is hosted by Google Forms, and our store locator and contact page embed Google Maps. Data submitted through those embeds is processed by Google under Google's own privacy policy in addition to ours.`,
        `Our home page shows the latest videos from our YouTube channel. To do this your browser loads thumbnail images from YouTube and requests our public channel feed through a third-party relay service. This happens automatically when the page loads, and those providers may see your IP address and browser details. No video is played and no YouTube cookie is set unless you click a video, which opens it on YouTube in a new tab under YouTube's own terms and privacy policy.`,
      ],
    },
    {
      heading: "Why we use it, and our lawful basis",
      body: [
        `We use your personal data only for the purposes set out below:`,
        {
          list: [
            "To perform our contract with you — processing a booking, collecting the advance and balance, arranging delivery or pickup, issuing invoices, and honouring warranty and service commitments.",
            "To respond to your enquiry — with your consent, given when you submit a form or message us.",
            "To meet legal obligations — GST invoicing, tax records, statutory accounting and responding to lawful requests from authorities.",
            "For our legitimate business interests — preventing fraud and payment abuse, securing the website, keeping accurate service records, and improving our products and service.",
            "For service reminders and offers — only where you have opted in. Every marketing message we send includes a way to opt out, and opting out never affects your warranty, service or support.",
          ],
        },
        `We do not sell your personal data. We do not rent, trade or otherwise make it available to third parties for their own marketing.`,
      ],
    },
    {
      heading: "Who we share it with",
      body: [
        `We share personal data only where it is necessary, and only with the following categories of recipient:`,
        {
          list: [
            "Payment gateways and banks — to process, verify, settle and refund payments, and to investigate a disputed transaction or chargeback.",
            "Manufacturers and authorised service partners (including EMotorad and Motovolt) — to register a warranty, raise a warranty claim, order a spare part or action a safety recall.",
            "Delivery and logistics partners — your name, address and phone number only, to complete a delivery.",
            "Financiers you choose — where you ask us to help you apply for EMI or consumer finance, we pass on only what that financier requires, and only with your consent.",
            "Professional advisers — our accountant and, where necessary, legal advisers, under a duty of confidentiality.",
            "Authorities — where we are required to disclose by law, court order or a valid request from a government or regulatory body.",
          ],
        },
        `We require every service provider to use your data only for the purpose we have given it to them for, and to keep it secure.`,
      ],
    },
    {
      heading: "Where your data is stored",
      body: [
        `Your personal data is stored in India. Some of the third-party services we use — for example Google Forms, Google Maps and email hosting — may process data on servers located outside India. Where that happens, the transfer is made in accordance with applicable Indian law and subject to that provider's contractual safeguards.`,
      ],
    },
    {
      heading: "How long we keep it",
      body: [
        {
          table: {
            head: ["Type of record", "Retention period"],
            rows: [
              [
                "Enquiries that do not lead to a sale",
                "Up to 24 months from last contact, then deleted",
              ],
              [
                "Order, invoice and payment records",
                "8 years from the end of the relevant financial year, as required under Indian tax and accounting law",
              ],
              [
                "Warranty and service history (frame / battery serial linked)",
                "For the life of the warranty plus 3 years, so that claims and safety recalls can be honoured",
              ],
              [
                "Marketing consent and opt-out records",
                "Until you withdraw consent, plus a minimal suppression record so we do not contact you again",
              ],
              [
                "Website technical logs",
                "Up to 12 months",
              ],
            ],
          },
        },
        `When a retention period ends we delete the data or anonymise it so it can no longer identify you.`,
      ],
    },
    {
      heading: "Cookies and website analytics",
      body: [
        `This website uses a small number of cookies and similar technologies:`,
        {
          list: [
            "Strictly necessary cookies — required for the site to function, to keep you signed in and to secure form submissions. These cannot be switched off.",
            "Analytics cookies — help us understand which pages and products people view, so we can improve the site. The data is aggregated.",
            "Third-party cookies — set by embedded Google Maps and Google Forms content, by YouTube if you click through to a video, and by social media links. These are controlled by those providers.",
          ],
        },
        `You can block or delete cookies through your browser settings. Blocking strictly necessary cookies may stop parts of the site from working. We do not use cookies to build advertising profiles.`,
      ],
    },
    {
      heading: "How we keep it secure",
      body: [
        {
          list: [
            "This website is served over HTTPS, so data in transit between your browser and the site is encrypted.",
            "Payment card and UPI credentials are handled exclusively by a PCI-DSS compliant payment gateway and are never stored on our systems.",
            "Access to customer records is restricted to staff who need it to do their job, and staff are instructed on handling customer data confidentially.",
            "Physical records kept at the showroom are stored securely and destroyed when no longer required.",
          ],
        },
        `No system is completely secure. If a personal data breach occurs that is likely to affect you, we will notify you and the Data Protection Board of India as required by law, and tell you what happened and what to do.`,
      ],
    },
    {
      heading: "Your rights",
      body: [
        `Under the Digital Personal Data Protection Act, 2023 you have the right to:`,
        {
          list: [
            "Access — ask for a summary of the personal data we hold about you and who we have shared it with.",
            "Correction — have inaccurate or incomplete data corrected or updated.",
            "Erasure — ask us to delete data we no longer need, subject to records we must keep by law (such as tax invoices).",
            "Withdraw consent — where we rely on consent, withdraw it at any time. Withdrawal does not affect processing already carried out, and does not affect your warranty or service entitlements.",
            "Grievance redressal — complain to us about how your data has been handled, and escalate to the Data Protection Board of India if you are not satisfied with our response.",
            "Nominate — nominate another person to exercise your rights in the event of your death or incapacity.",
          ],
        },
        `To exercise any of these rights, email ${BUSINESS.email} or call ${BUSINESS.phone}. We may ask you to verify your identity before we act, to make sure we are not disclosing your data to someone else. We respond within 30 days.`,
      ],
    },
    {
      heading: "Children's data",
      body: [
        `Our website and services are intended for adults aged 18 and over. We do not knowingly collect personal data from children. Where a product is bought for a child rider, we collect the data of the purchasing parent or guardian, not the child, beyond a first name where it is needed for fitment.`,
        `If you believe we hold personal data belonging to a child, contact us and we will delete it promptly.`,
      ],
    },
    {
      heading: "Changes to this policy",
      body: [
        `We may update this Privacy Policy as our business or the law changes. The current version is always published on this page with the date it was last updated at the top. Where a change materially affects your rights, we will bring it to your attention directly.`,
      ],
    },
  ];

  return (
    <PolicyPage
      title="Privacy Policy"
      currentPath="/privacy"
      intro={`We collect customer details in order to sell, deliver, service and warranty electric bicycles. This policy explains exactly what we collect, why, who sees it, how long we keep it, and how you can get it corrected or deleted.`}
      sections={sections}
    />
  );
}
